import { JobBillingStatus, Proposal } from "generated/graphql";
import { Card, Divider, Grid } from "@mui/material";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Job } from "generated/graphql";
import ProjectManagerSelection from "./ProjectManagerSelection";
import NotesSection from "./NotesSection";
import ChangeOrders from "modules/proposals/ChangeOrders/ChangeOrders";
import BillingStatusSelection from "./BillingStatusSelection";
import { useState } from "react";
import ProjectFileExplorerInput from "modules/ProjectFiles/ProjectFileExplorerInput";
import useUpdateJobProjectFileFolderId from "hooks/jobs/useUpdateJobProjectFileFolderId";
import EditJobExternalIdModal from "./EditJobExternalIdModal";
import JobCostProgressPanel from "./JobCostProgressPanel";
import useJobDepositStatus from "hooks/jobs/useJobDepositStatus";
import useSetJobDepositCollected from "hooks/jobs/useSetJobDepositCollected";
import MDButton from "components/MDButton";

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <MDBox
      display="grid"
      gridTemplateColumns={{ xs: "120px 1fr", md: "140px minmax(0, 1fr)" }}
      alignItems="center"
      columnGap={1.5}
      rowGap={0}
      py={0.2}
    >
      <MDTypography
        variant="caption"
        color="text"
        sx={{ textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.05 }}
      >
        {label}
      </MDTypography>
      <MDBox minWidth={0}>{children}</MDBox>
    </MDBox>
  );
}

export default function CompanySection({ job }: { job: Job }) {
  const {
    company,
    contact,
    address,
    userId: projectManager,
    billingStatus,
    id: jobId,
    datePushedToQuickbooks,
    notes,
    proposalStage,
    externalId: jobNumber,
    proposal,
    changeOrders,
    projectFilesFolderId,
  } = job;
  const [folderId, setFolderId] = useState(projectFilesFolderId);
  const [updateJobProjectFileFolderId] = useUpdateJobProjectFileFolderId();
  const { data: depositStatusData } = useJobDepositStatus(String(jobId));
  const [setJobDepositCollected, { loading: depositLoading }] = useSetJobDepositCollected();
  const depositCollectedAt = depositStatusData?.getJob?.depositCollectedAt;
  const depositEligible =
    [JobBillingStatus.PARTIAL_BILLED, JobBillingStatus.DEPOSIT_BILLED].includes(
      depositStatusData?.getJob?.billingStatus as JobBillingStatus
    ) || Boolean(depositCollectedAt);

  const handleProjectFilesFolderSelect = (folderId: string) => {
    setFolderId(folderId);
    updateJobProjectFileFolderId({
      variables: {
        id: jobId,
        projectFilesFolderId: folderId,
      },
    });
  };

  const handleDepositToggle = async (collected: boolean) => {
    await setJobDepositCollected({
      variables: {
        id: String(jobId),
        collected,
      },
    });
  };

  return (
    <Card>
      <MDBox p={3} pl={4}>
        <Grid container spacing={1.5} alignItems="flex-start">
          <Grid item xs={12} lg={6}>
            <MDBox display="flex" flexDirection="column" gap={0.2}>
              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{ letterSpacing: "0.02em", lineHeight: 1.1, mb: 0.25 }}
              >
                Job Info
              </MDTypography>

              <InfoRow label="Job Number">
                <MDBox display="flex" alignItems="center" justifyContent="flex-start" gap={0.5}>
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    sx={{ textAlign: "left", lineHeight: 1.1 }}
                  >
                    {jobNumber}
                  </MDTypography>
                  <EditJobExternalIdModal jobId={jobId} currentExternalId={jobNumber} />
                </MDBox>
              </InfoRow>

              <InfoRow label="Company">
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ textAlign: "left", lineHeight: 1.1 }}
                >
                  {company.name}
                </MDTypography>
              </InfoRow>

              <InfoRow label="Stage">
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ textAlign: "left", lineHeight: 1.1, wordBreak: "break-word" }}
                >
                  {proposalStage?.name}
                </MDTypography>
              </InfoRow>

              <InfoRow label="Address">
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ textAlign: "left", lineHeight: 1.15, wordBreak: "break-word" }}
                >
                  {address.line1}
                  {address.line2 ? `, ${address.line2}` : ""}
                  {`, ${address.city}, ${address.state} ${address.zip}, ${address.country}`}
                </MDTypography>
              </InfoRow>

              <MDBox mt={0.5}>
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ letterSpacing: "0.02em", lineHeight: 1.1, mb: 0.15 }}
                >
                  Contact Info
                </MDTypography>
              </MDBox>

              <InfoRow label="Name">
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ textAlign: "left", lineHeight: 1.1 }}
                >
                  {[contact.firstName, contact.lastName].filter(Boolean).join(" ")}
                </MDTypography>
              </InfoRow>

              <InfoRow label="Email">
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ textAlign: "left", lineHeight: 1.1, wordBreak: "break-word" }}
                >
                  {contact.email || "—"}
                </MDTypography>
              </InfoRow>

              <InfoRow label="Phone">
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ textAlign: "left", lineHeight: 1.1 }}
                >
                  {contact.phone || "—"}
                </MDTypography>
              </InfoRow>

              <MDBox mt={0.5}>
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ letterSpacing: "0.02em", lineHeight: 1.1, mb: 0.15 }}
                >
                  Job Settings
                </MDTypography>
              </MDBox>

              <InfoRow label="Project Manager">
                <MDBox display="flex" justifyContent="flex-start">
                  <ProjectManagerSelection
                    jobId={jobId}
                    projectManager={projectManager}
                    sx={{ width: { xs: "100%", sm: "240px" } }}
                  />
                </MDBox>
              </InfoRow>

              <InfoRow label="Billing Status">
                <MDBox display="flex" justifyContent="flex-start">
                  <BillingStatusSelection
                    jobId={jobId}
                    billingStatus={billingStatus}
                    sx={{ width: { xs: "100%", sm: "240px" } }}
                  />
                </MDBox>
              </InfoRow>

              <InfoRow label="Project Files">
                <MDBox display="flex" justifyContent="flex-start">
                  <ProjectFileExplorerInput
                    submitFn={handleProjectFilesFolderSelect}
                    folderId={folderId}
                    compact
                    align="left"
                    showCompactLabel={false}
                    sx={{ width: "240px", maxWidth: "100%" }}
                  />
                </MDBox>
              </InfoRow>

              {datePushedToQuickbooks && (
                <InfoRow label="QuickBooks Sync">
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    sx={{ textAlign: "left", lineHeight: 1.1 }}
                  >
                    {datePushedToQuickbooks}
                  </MDTypography>
                </InfoRow>
              )}

              {depositEligible && (
                <InfoRow label="Deposit">
                  <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{ textAlign: "left", lineHeight: 1.1 }}
                    >
                      {depositCollectedAt
                        ? `Collected ${dayjs(depositCollectedAt).format("MM/DD/YYYY")}`
                        : "Not collected"}
                    </MDTypography>
                    <MDButton
                      size="small"
                      color={depositCollectedAt ? "secondary" : "success"}
                      disabled={depositLoading}
                      onClick={() => handleDepositToggle(!depositCollectedAt)}
                    >
                      {depositCollectedAt ? "Clear" : "Mark Collected"}
                    </MDButton>
                  </MDBox>
                </InfoRow>
              )}
            </MDBox>
          </Grid>
          <Grid item xs={12} lg={6}>
            <JobCostProgressPanel job={job} />
          </Grid>
        </Grid>
      </MDBox>
      <Divider />
      <MDBox p={3} pl={4}>
        <Grid container>
          <Grid item xs={12}>
            <ChangeOrders
              changeOrders={changeOrders}
              jobId={jobId}
              proposal={proposal as Proposal}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Divider />
      <NotesSection notes={notes} jobId={jobId} />
    </Card>
  );
}
